import { Edit } from "@mui/icons-material";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useMarkTimesheetsPaid, useUpdateTimesheetEntry } from "utils/employeeService";
import { getUserType, isSuperAdmin } from "utils/permissionUtils";
import {
  entryBreakMinutes,
  entryDurationMinutes,
  formatDuration,
  isOpenEntry,
  normalizePaymentStatus,
  PAYMENT_METHOD,
  PAYMENT_STATUS,
  paymentMethodLabel,
} from "utils/timesheetUtils";

const formatStamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd MMM yyyy hh:mm a");
};

const formatPaidOn = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return format(date, "dd MMM yyyy");
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const todayInputDate = () => format(new Date(), "yyyy-MM-dd");

const canAdjustClockTimes = () => isSuperAdmin() || getUserType() === "SUPER_ADMIN";

const TimesheetEntriesTable = ({
  entries = [],
  isLoading,
  showEmployee = true,
  onTogglePayment,
  isUpdatingPayment,
}) => {
  const canAdjust = canAdjustClockTimes();
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    clockIn: "",
    clockOut: "",
    breakMinutes: "0",
  });
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payIds, setPayIds] = useState([]);
  const [payForm, setPayForm] = useState({
    paymentMethod: PAYMENT_METHOD.BANK,
    paidOn: todayInputDate(),
    note: "",
  });

  const updateEntryMutation = useUpdateTimesheetEntry({
    onSuccess: () => {
      toast.success("Clock times updated");
      setEditEntry(null);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.errorMessage || "Failed to update clock times"),
  });

  const markPaidMutation = useMarkTimesheetsPaid({
    onSuccess: (_, variables) => {
      toast.success(
        variables?.ids?.length > 1
          ? `${variables.ids.length} entries marked as paid`
          : "Entry marked as paid"
      );
      setPayDialogOpen(false);
      setRowSelection({});
    },
    onError: (error) =>
      toast.error(error?.response?.data?.errorMessage || "Failed to mark entries as paid"),
  });

  const filteredEntries = useMemo(() => {
    return (entries || []).filter((entry) => {
      const status = normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus);
      if (paymentFilter === PAYMENT_STATUS.PAID && status !== PAYMENT_STATUS.PAID) return false;
      if (paymentFilter === PAYMENT_STATUS.UNPAID && status !== PAYMENT_STATUS.UNPAID) return false;

      const stamp = new Date(entry.clockIn || entry.clockInAt);
      if (Number.isNaN(stamp.getTime())) return true;
      if (fromDate) {
        const from = new Date(`${fromDate}T00:00:00`);
        if (stamp < from) return false;
      }
      if (toDate) {
        const to = new Date(`${toDate}T23:59:59`);
        if (stamp > to) return false;
      }
      return true;
    });
  }, [entries, fromDate, paymentFilter, toDate]);

  const selectedPayableIds = useMemo(() => {
    return filteredEntries
      .filter((entry) => rowSelection[String(entry.id)])
      .filter(
        (entry) =>
          !isOpenEntry(entry) &&
          normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus) === PAYMENT_STATUS.UNPAID
      )
      .map((entry) => entry.id);
  }, [filteredEntries, rowSelection]);

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      clockIn: toDateTimeLocal(entry.clockIn || entry.clockInAt),
      clockOut: toDateTimeLocal(entry.clockOut || entry.clockOutAt),
      breakMinutes: String(entryBreakMinutes(entry)),
    });
  };

  const openPayDialog = (ids) => {
    if (!ids?.length) {
      toast.error("Select completed unpaid entries first");
      return;
    }
    setPayIds(ids);
    setPayForm({
      paymentMethod: PAYMENT_METHOD.BANK,
      paidOn: todayInputDate(),
      note: "",
    });
    setPayDialogOpen(true);
  };

  const savePay = () => {
    if (!payForm.paidOn) {
      toast.error("Select the payment date");
      return;
    }
    markPaidMutation.mutate({
      ids: payIds,
      paymentMethod: payForm.paymentMethod,
      paidOn: payForm.paidOn,
      note: payForm.note?.trim() || "",
    });
  };

  const saveEdit = () => {
    if (!editForm.clockIn) {
      toast.error("Clock in time is required");
      return;
    }
    if (editForm.clockOut && new Date(editForm.clockOut) < new Date(editForm.clockIn)) {
      toast.error("Clock out cannot be before clock in");
      return;
    }
    const breakMinutes = Math.max(0, Number(editForm.breakMinutes) || 0);
    updateEntryMutation.mutate({
      id: editEntry.id,
      clockIn: new Date(editForm.clockIn).toISOString(),
      clockOut: editForm.clockOut ? new Date(editForm.clockOut).toISOString() : null,
      breakMinutes,
    });
  };

  const columns = useMemo(() => {
    const cols = [];
    if (showEmployee) {
      cols.push({
        accessorKey: "employeeName",
        header: "Employee",
        size: 160,
        accessorFn: (row) => row.employeeName || row.employee?.name || "-",
      });
    }
    cols.push(
      {
        id: "clockIn",
        header: "Clock in",
        size: 160,
        accessorFn: (row) => row.clockIn || row.clockInAt,
        Cell: ({ row }) => formatStamp(row.original.clockIn || row.original.clockInAt),
      },
      {
        id: "clockOut",
        header: "Clock out",
        size: 160,
        accessorFn: (row) => row.clockOut || row.clockOutAt,
        Cell: ({ row }) =>
          isOpenEntry(row.original) ? (
            <Chip size="small" color="success" label="In progress" />
          ) : (
            formatStamp(row.original.clockOut || row.original.clockOutAt)
          ),
      },
      {
        id: "breakMinutes",
        header: "Break",
        size: 80,
        accessorFn: (row) => entryBreakMinutes(row),
        Cell: ({ row }) => formatDuration(entryBreakMinutes(row.original)),
      },
      {
        id: "duration",
        header: "Worked",
        size: 90,
        accessorFn: (row) => entryDurationMinutes(row),
        Cell: ({ row }) => formatDuration(entryDurationMinutes(row.original)),
      },
      {
        id: "paymentStatus",
        header: "Payment",
        size: 200,
        accessorFn: (row) => normalizePaymentStatus(row.paymentStatus ?? row.paidStatus),
        Cell: ({ row }) => {
          const entry = row.original;
          const status = normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus);
          const paid = status === PAYMENT_STATUS.PAID;
          const method = paymentMethodLabel(entry.paymentMethod);
          const paidOn = formatPaidOn(entry.paidOn);
          return (
            <div className="flex flex-col items-start gap-0.5">
              <Tooltip
                title={
                  paid
                    ? "Mark as unpaid"
                    : isOpenEntry(entry)
                    ? "Clock out before marking paid"
                    : "Mark as paid"
                }
                arrow
              >
                <Chip
                  size="small"
                  label={paid ? "Paid" : "Unpaid"}
                  color={paid ? "success" : "warning"}
                  onClick={() => {
                    if (isOpenEntry(entry) || isUpdatingPayment) return;
                    if (paid) {
                      onTogglePayment?.(entry, PAYMENT_STATUS.UNPAID);
                    } else {
                      openPayDialog([entry.id]);
                    }
                  }}
                  disabled={isUpdatingPayment || isOpenEntry(entry)}
                  sx={{ cursor: isOpenEntry(entry) ? "default" : "pointer" }}
                />
              </Tooltip>
              {paid && (entry.paymentMethod || entry.paidOn) ? (
                <span className="text-[11px] text-app-text-secondary">
                  {entry.paymentMethod ? method : ""}
                  {entry.paymentMethod && entry.paidOn ? " · " : ""}
                  {paidOn}
                  {entry.paymentNote ? ` · ${entry.paymentNote}` : ""}
                </span>
              ) : null}
            </div>
          );
        },
      }
    );
    if (canAdjust) {
      cols.push({
        id: "adjust",
        header: "Adjust",
        size: 70,
        enableSorting: false,
        Cell: ({ row }) => (
          <Tooltip title="Adjust clock times" arrow>
            <IconButton size="small" onClick={() => openEdit(row.original)}>
              <Edit fontSize="small" className="text-primary" />
            </IconButton>
          </Tooltip>
        ),
      });
    }
    return cols;
  }, [canAdjust, isUpdatingPayment, onTogglePayment, showEmployee]);

  const table = useMaterialReactTable({
    columns,
    data: filteredEntries,
    getRowId: (row) => String(row.id),
    enableRowSelection: (row) => !isOpenEntry(row.original),
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableStickyHeader: true,
    enablePagination: true,
    onRowSelectionChange: setRowSelection,
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      pagination: { pageIndex: 0, pageSize: 20 },
      sorting: [{ id: "clockIn", desc: true }],
    },
    muiTableContainerProps: {
      sx: { maxHeight: "60vh", minHeight: "40vh", overflowY: "auto" },
    },
    state: {
      isLoading,
      showProgressBars: isLoading,
      rowSelection,
    },
    renderTopToolbarCustomActions: () => (
      <div className="flex flex-wrap items-end gap-3 py-1">
        <Button
          variant="contained"
          size="small"
          disabled={!selectedPayableIds.length || markPaidMutation.isLoading}
          onClick={() => openPayDialog(selectedPayableIds)}
        >
          Mark paid{selectedPayableIds.length ? ` (${selectedPayableIds.length})` : ""}
        </Button>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Payment</InputLabel>
          <Select
            label="Payment"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value={PAYMENT_STATUS.PAID}>Paid</MenuItem>
            <MenuItem value={PAYMENT_STATUS.UNPAID}>Unpaid</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        {(paymentFilter !== "ALL" || fromDate || toDate) && (
          <Button
            size="small"
            onClick={() => {
              setPaymentFilter("ALL");
              setFromDate("");
              setToDate("");
            }}
          >
            Clear filters
          </Button>
        )}
      </div>
    ),
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Dialog open={Boolean(editEntry)} onClose={() => setEditEntry(null)} fullWidth maxWidth="sm">
        <DialogTitle>Adjust clock times</DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-4">
          <p className="text-sm text-app-text-secondary">
            {editEntry?.employeeName || editEntry?.employee?.name || "Employee"} — correct a missed
            or wrong clock time. Worked hours exclude break.
          </p>
          <TextField
            label="Clock in"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={editForm.clockIn}
            onChange={(e) => setEditForm((prev) => ({ ...prev, clockIn: e.target.value }))}
          />
          <TextField
            label="Clock out"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={editForm.clockOut}
            onChange={(e) => setEditForm((prev) => ({ ...prev, clockOut: e.target.value }))}
            helperText="Leave empty if the shift is still in progress"
          />
          <TextField
            label="Break (minutes)"
            type="number"
            inputProps={{ min: 0 }}
            value={editForm.breakMinutes}
            onChange={(e) => setEditForm((prev) => ({ ...prev, breakMinutes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEntry(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={updateEntryMutation.isLoading || updateEntryMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mark as paid</DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-4">
          <p className="text-sm text-app-text-secondary">
            Record how{" "}
            {payIds.length === 1 ? "this entry was" : `these ${payIds.length} entries were`} paid.
            In-progress shifts cannot be included.
          </p>
          <FormControl>
            <FormLabel>Paid via</FormLabel>
            <RadioGroup
              row
              value={payForm.paymentMethod}
              onChange={(e) => setPayForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
            >
              <FormControlLabel value={PAYMENT_METHOD.BANK} control={<Radio />} label="Bank" />
              <FormControlLabel value={PAYMENT_METHOD.CASH} control={<Radio />} label="Cash" />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Payment date"
            type="date"
            required
            InputLabelProps={{ shrink: true }}
            value={payForm.paidOn}
            onChange={(e) => setPayForm((prev) => ({ ...prev, paidOn: e.target.value }))}
          />
          <TextField
            label="Note (optional)"
            placeholder="e.g. Salary transfer, cash on 15th"
            value={payForm.note}
            onChange={(e) => setPayForm((prev) => ({ ...prev, note: e.target.value }))}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={savePay}
            disabled={markPaidMutation.isLoading || markPaidMutation.isPending}
          >
            Mark paid
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimesheetEntriesTable;
